import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    User,
    Phone,
    Mail,
    MapPin,
    CreditCard,
    Shield,
    RefreshCw,
    Calendar,
    Clock,
    PhoneCall,
    Download,
    Edit3,
    Save,
    Wallet,
    Banknote,
    Smartphone,
    Star,
    Loader2,
} from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../state/store.ts";
import { CustomerHeader } from '../../components/ui/customers/header.jsx';
import SectionCard from '../../components/ui/customers/sectioncard.tsx';
import WalletSection from '../../components/ui/customers/wallet.tsx';
import Pill from '../../components/ui/customers/pill.tsx';
import { maskCard, fmtDate, fmtDuration, estimateMinutes } from '../../utilities/conversions.js';
import { money } from "../../utilities/currency.js";
import { setActiveTab } from '../../state/tab/tabSlice.ts';
import { statsBuffer } from 'framer-motion';




// Små testcases (ikke‑blokkerende)
try {
    console.assert(money(149).startsWith("kr"), "money NOK prefix");
    console.assert(maskCard("4242").endsWith("4242"), "mask last4");
    console.assert(fmtDuration(125) === "2 t 5 min", "duration long");
    console.assert(estimateMinutes(290, 29) === 10, "estimate minutes");
    // ekstra test for CSV linjeskift
    console.assert(["a", "b"].join("\n").includes("\n"), "csv newline join");
} catch (e) {
    console.warn("helper tests warning", e);
}

/************** DUMMY DATA **************/
const vippsProfile = {
    name: "Kari Nordmann",
    email: "kari@example.no",
    phone: "+47 99887766",
    address1: "Storgata 12",
    address2: "",
    city: "Oslo",
    zip: "0184",
    country: "Norge",
};
const stripeProfile = {
    name: "Ola Nordmann",
    email: "ola@example.no",
    phone: "+47 91234567",
    address1: "Markveien 3",
    address2: "",
    city: "Bergen",
    zip: "5014",
    country: "Norge",
};

const defaultCards = [
    { brand: "Visa", last4: "4242", expMonth: 12, expYear: 2027, default: true },
    { brand: "Mastercard", last4: "5454", expMonth: 8, expYear: 2026, default: false },
];

const upcomingBookingsSeed = [
    { id: "b1", when: new Date(Date.now() + 86400000).toISOString(), advisor: "Sondre", type: "Telefon", lengthMin: 20 },
    { id: "b2", when: new Date(Date.now() + 172800000).toISOString(), advisor: "Ingrid", type: "Video", lengthMin: 30 },
];
const historyCallsSeed = [
    { id: "h1", when: new Date(Date.now() - 86400000 * 2).toISOString(), advisor: "Mats", type: "Telefon", lengthMin: 15, cost: 249 },
    { id: "h2", when: new Date(Date.now() - 86400000 * 6).toISOString(), advisor: "Linn", type: "Chat", lengthMin: 25, cost: 399 },
    { id: "h3", when: new Date(Date.now() - 86400000 * 9).toISOString(), advisor: "Anette", type: "Video", lengthMin: 10, cost: 179 },
];

/************** UI BYGGEKLOSSER **************/

function Field({ label, children }: any) {
    return (
        <label className="grid gap-1 text-sm">
            <span className="text-gray-600">{label}</span>
            {children}
        </label>
    );
}

function Stars({ value = 0, onChange }: { value?: number; onChange?: (n: number) => void }) {
    return (
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} onClick={() => onChange && onChange(n)} className="text-amber-500" title={`${n} stjerner`}>
                    <Star className={`h-5 w-5 ${n <= value ? '' : 'opacity-30'}`} />
                </button>
            ))}
        </div>
    );
}



type customerDashboardProps = {
    currentUser: any,
    onLogout: string,
    baseUrl: string,
}

function CustomerDashboard({ currentUser, onLogout, baseUrl }: customerDashboardProps) {
    const activeTab = useSelector((state: RootState) => state.tab.activeTab);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    useEffect(() => {
        if (activeTab === "Home") {
            dispatch(setActiveTab("Profil"));
            navigate("/");
        }
    }, [activeTab, dispatch, navigate]);
    useEffect(() => {
        if (!currentUser || currentUser.role !== 'customer') {
            navigate('/'); // Redirect to home if not logged in or not an customer
        }
    }, [currentUser, navigate]);
    // Profil
    const [profile, setProfile] = useState({
        name: "",
        email: "",
        phone: "",
        address1: "",
        address2: "",
        city: "",
        zip: "",
        country: "Norge",
    });
    const [syncedFrom, setSyncedFrom] = useState<string>("");

    // Kort
    const [cards, setCards] = useState(defaultCards);

    // Lommebok
    const [balance, setBalance] = useState(299); // NOK
    const [method, setMethod] = useState<'vipps' | 'card'>("vipps");
    const [preset, setPreset] = useState<number | null>(399);
    const [custom, setCustom] = useState<number | ''>('');
    const [topupLoading, setTopupLoading] = useState(false);
    const [txs, setTxs] = useState<Array<{ id: string, when: string, amount: number, method: string }>>([
        { id: 't1', when: new Date(Date.now() - 86400000 * 3).toISOString(), amount: 199, method: 'Vipps' },
        { id: 't2', when: new Date(Date.now() - 86400000 * 7).toISOString(), amount: 399, method: 'Kort' },
    ]);
    const topupPresets = [149, 399, 999];

    // Bookinger og historikk
    const [upcoming, setUpcoming] = useState(upcomingBookingsSeed);
    const [history, setHistory] = useState(historyCallsSeed.map(h => ({ ...h, rating: 0, review: '' })));



    function syncFrom(source: "Vipps" | "Stripe") {
        const data = source === "Vipps" ? vippsProfile : stripeProfile;
        setProfile(data as any);
        setSyncedFrom(source);
        setTimeout(() => setSyncedFrom(""), 2500);
    }

    function setDefaultCard(last4: string) {
        setCards((list) => list.map((c) => ({ ...c, default: c.last4 === last4 })));
    }

    // Top‑up logikk
    const amountToTopup = preset ?? (Number(custom) || 0);


    // CSV eksport for historikk
    const csvHref = useMemo(() => {
        const header = ["Dato", "Veileder", "Type", "Lengde(min)", "Kostnad(NOK)", "Rating", "Anmeldelse"];
        const rows = history.map((h) => [fmtDate(h.when), h.advisor, h.type, String(h.lengthMin), String(h.cost), String(h.rating || ''), (h.review || '')]);
        const csv = [header, ...rows]
            .map((r) => r.map((x) => `"${String(x).replaceAll('"', '""')}"`).join(","))
            .join("\n");
        return `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    }, [history]);

    const avgRating = useMemo(() => {
        const rated = history.filter(h => (h.rating || 0) > 0);
        if (rated.length === 0) return 0;
        return Math.round((rated.reduce((s, h) => s + (h.rating || 0), 0) / rated.length) * 10) / 10;
    }, [history]);

    if (!currentUser || currentUser.role !== 'customer') {
        return null; // render nothing while redirecting
    }



    return (
        <>
            <CustomerHeader currentUser={currentUser} onLogout={onLogout} />
            <div className="mx-auto max-w-6xl p-6">
                {/* Header */}
                {/* <header className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="grid h-10 w-10 place-items-center rounded-full bg-gradient-to-br from-purple-200 to-pink-200 text-purple-800">
                        <User className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="text-xl font-extrabold text-purple-900">{currentUser.name}</div>
                        <div className="text-xs text-gray-600">Administrer profil, betaling og avtaler</div>
                    </div>
                </div>
                <Translator />
                <Pill tone="green"><CheckCircle2 className="h-3 w-3" /> Innlogget</Pill>
            </header> */}



                {activeTab === "Profil" && (
                    <SectionCard
                        title="Profil"
                        icon={<User className="h-5 w-5 text-purple-700" />}
                        actions={
                            <div className="flex items-center gap-2">
                                <button onClick={() => syncFrom("Vipps")} className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm"><RefreshCw className="h-4 w-4" /> Synk fra Vipps</button>
                                <button onClick={() => syncFrom("Stripe")} className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm"><RefreshCw className="h-4 w-4" /> Synk fra Stripe</button>
                                <button className="inline-flex items-center gap-2 rounded-full bg-purple-600 px-4 py-2 text-white text-sm font-semibold"><Save className="h-4 w-4" /> Lagre</button>
                            </div>
                        }
                    >
                        {syncedFrom && (
                            <div className="mb-4 rounded-xl bg-green-50 p-3 text-green-800 text-sm">Profil oppdatert fra {syncedFrom}.</div>
                        )}
                        <div className="grid gap-4 md:grid-cols-2">
                            <Field label="Navn">
                                <input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="rounded-xl border px-3 py-2" />
                            </Field>
                            <Field label="E‑post">
                                <div className="flex items-center gap-2">
                                    <Mail className="h-4 w-4 text-purple-700" />
                                    <input value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} className="flex-1 rounded-xl border px-3 py-2" />
                                </div>
                            </Field>
                            <Field label="Telefon">
                                <div className="flex items-center gap-2">
                                    <Phone className="h-4 w-4 text-purple-700" />
                                    <input value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} className="flex-1 rounded-xl border px-3 py-2" />
                                </div>
                            </Field>
                            <Field label="Adresse linje 1">
                                <div className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-purple-700" />
                                    <input value={profile.address1} onChange={(e) => setProfile({ ...profile, address1: e.target.value })} className="flex-1 rounded-xl border px-3 py-2" />
                                </div>
                            </Field>
                            <Field label="Adresse linje 2 (valgfri)">
                                <input value={profile.address2} onChange={(e) => setProfile({ ...profile, address2: e.target.value })} className="rounded-xl border px-3 py-2" />
                            </Field>
                            <Field label="Postnummer">
                                <input value={profile.zip} onChange={(e) => setProfile({ ...profile, zip: e.target.value })} className="rounded-xl border px-3 py-2" />
                            </Field>
                            <Field label="Sted">
                                <input value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} className="rounded-xl border px-3 py-2" />
                            </Field>
                            <Field label="Land">
                                <input value={profile.country} onChange={(e) => setProfile({ ...profile, country: e.target.value })} className="rounded-xl border px-3 py-2" />
                            </Field>
                        </div>
                    </SectionCard>
                )}

                {activeTab === "Betaling" && (
                    <SectionCard title="Betalingsmetoder" icon={<CreditCard className="h-5 w-5 text-purple-700" />}>
                        <div className="grid gap-4">
                            {cards.map((c) => (
                                <div key={c.last4} className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-xl border p-3 ${c.default ? 'border-purple-600' : 'border-gray-200'}`}>
                                    <div className="flex items-center gap-3">
                                        <div className="grid h-10 w-10 place-items-center rounded-xl bg-purple-50 text-purple-800"><CreditCard className="h-5 w-5" /></div>
                                        <div>
                                            <div className="font-semibold">{c.brand} {maskCard(c.last4)}</div>
                                            <div className="text-xs text-gray-500">Utløper {String(c.expMonth).padStart(2, '0')}/{c.expYear}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {c.default ? (
                                            <Pill tone="green"><Shield className="h-3 w-3" /> Standard</Pill>
                                        ) : (
                                            <button onClick={() => setDefaultCard(c.last4)} className="rounded-full border px-3 py-1 text-sm">Sett som standard</button>
                                        )}
                                        <button className="rounded-full border px-3 py-1 text-sm"><Edit3 className="h-4 w-4 mr-1 inline" /> Rediger</button>
                                    </div>
                                </div>
                            ))}
                            <div className="text-xs text-gray-500">For kortbetaling må kort registreres på ditt mobilnummer.</div>
                        </div>
                    </SectionCard>
                )}

                {activeTab === "Lommebok" && (
                    <>
                        <WalletSection amountToTopup={amountToTopup} baseUrl={baseUrl} currentUser={currentUser} />
                    </>
                )}

                {activeTab === "Avtaler" && (
                    <SectionCard title="Mine bookede avtaler" icon={<Calendar className="h-5 w-5 text-purple-700" />}>
                        {upcoming.length === 0 ? (
                            <div className="text-gray-600">Ingen kommende avtaler.</div>
                        ) : (
                            <ul className="space-y-3">
                                {upcoming.map((b) => (
                                    <li key={b.id} className="rounded-xl border p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                        <div>
                                            <div className="font-semibold">{b.advisor}</div>
                                            <div className="text-sm text-gray-600 flex items-center gap-2"><Calendar className="h-4 w-4" /> {fmtDate(b.when)} • {b.type} • {fmtDuration(b.lengthMin)}</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <button className="rounded-full bg-purple-600 px-4 py-2 text-white hover:bg-purple-700">Bli med</button>
                                            <button className="rounded-full border px-4 py-2 hover:border-purple-500">Flytt</button>
                                            <button className="rounded-full border px-4 py-2 hover:border-purple-500">Avbestill</button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </SectionCard>
                )}

                {activeTab === "Historikk" && (
                    <SectionCard
                        title="Gjennomførte samtaler"
                        icon={<PhoneCall className="h-5 w-5 text-purple-700" />}
                        actions={<a href={csvHref} download="mysticconnect_samtalehistorikk.csv" className="inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm"><Download className="h-4 w-4" /> Last ned CSV</a>}
                    >
                        <div className="mb-3 flex items-center gap-2">
                            <span className="text-sm text-gray-600">Snittvurdering:</span>
                            <Stars value={avgRating} />
                            <span className="text-sm text-gray-600">{avgRating || '-'} / 5</span>
                        </div>
                        {history.length === 0 ? (
                            <div className="text-gray-600">Ingen historikk enda.</div>
                        ) : (
                            <ul className="space-y-3">
                                {history.map((h, idx) => (
                                    <li key={h.id} className="rounded-xl border p-3 flex flex-col gap-3">
                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                            <div>
                                                <div className="font-semibold">{h.advisor}</div>
                                                <div className="text-sm text-gray-600 flex items-center gap-2"><Clock className="h-4 w-4" /> {fmtDate(h.when)} • {h.type} • {fmtDuration(h.lengthMin)}</div>
                                            </div>
                                            <div className="text-purple-900 font-semibold">{money(h.cost)}</div>
                                        </div>

                                        {/* Rating/anmeldelse */}
                                        <div className="rounded-lg border p-3">
                                            <div className="flex items-center justify-between">
                                                <div className="text-sm font-semibold text-purple-900">Din vurdering</div>
                                                {h.rating ? <Pill tone="green">Takk! Lagret</Pill> : <Pill tone="amber">Ikke vurdert</Pill>}
                                            </div>
                                            <div className="mt-2 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                                                <Stars value={h.rating || 0} onChange={(n) => setHistory((list) => list.map((x, i) => i === idx ? { ...x, rating: n } : x))} />
                                                <textarea value={h.review || ''} onChange={(e) => setHistory((list) => list.map((x, i) => i === idx ? { ...x, review: e.target.value } : x))} rows={2} className="flex-1 rounded-lg border px-3 py-2" placeholder="Skriv en kort omtale (valgfritt)" />
                                                <button className="rounded-full bg-purple-600 px-4 py-2 text-white hover:bg-purple-700">Lagre</button>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </SectionCard>
                )}
            </div>
        </>
    );

}

export default CustomerDashboard;